/**
 * DAE API Service. Fully SDK-backed with business logic for dashboard stats and notification
 * parsing.
 */

import type {
  Study,
  DaeStudiesResponse,
  Notification,
  DaeStudyDetailResponse as _DaeStudyDetail,
} from "@/generated/sdk"
import { ingestionApiService } from "./ingestionApi"
import { sdk, sdkCall } from "./sdk"

export interface DashboardStats {
  studies: { total: number; active: number; health: StudyHealthBuckets }
  dtas: { total: number; active: number; pendingReviews: number; pendingValidations: number }
  vendors: { active: number }
}

/**
 * Studies bucketed by recent ingestion-run pass rate (server-side aggregation). Only studies with
 * at least one finished Live Run in the health window are counted, so the buckets sum to studies
 * actively receiving data - not the whole portfolio.
 *
 * Buckets by pass rate: below 50% (more failed than passed) is `atRisk`; 50 to 90% inclusive is
 * `watch`; above 90% is `healthy`.
 */
export interface StudyHealthBuckets {
  atRisk: number
  watch: number
  healthy: number
}

/**
 * Why a study needs attention: how many of its recent ingestion runs failed (or only partially
 * completed) and how many records were quarantined (failed data-quality validation). Used to render
 * a clear inline hint in the My Studies table, not just flag the row.
 */
export interface StudyAttention {
  failedRuns: number
  quarantinedRecords: number
}

// Only runs from the last N days count toward "needs attention", so a study drops off the list once
// its failures age out rather than being flagged forever.
const ATTENTION_WINDOW_DAYS = 14
const ATTENTION_WINDOW_MS = ATTENTION_WINDOW_DAYS * 24 * 60 * 60 * 1000

// Max concurrent per-study runs lookups. The attention summary fans out one runs call per granted
// study; cap the in-flight batch so a DAE with hundreds of studies can't fire hundreds of requests
// at once (browsers already queue past ~6/host, but this bounds the load we put on the proxy). The
// real fix is the backend rollup — see getStudiesNeedingAttention and #1004.
const ATTENTION_FETCH_CONCURRENCY = 8

export type { Study, Notification } from "@/generated/sdk"

class DAEApiService {
  /**
   * Returns a map of study ID → attention summary for every study that needs attention — i.e. has
   * at least one ingestion run that FAILED or only PARTIALLY_COMPLETED, or that quarantined records
   * (rows that failed data-quality / conformance validation). Only flagged studies appear in the
   * map. This is the actionable "something on the pipeline broke, go look" signal shared by the
   * dashboard "Need Attention" tile (which uses the map size) and the My Studies filter + inline
   * hint (which use each entry's counts) — one definition feeding both. Note the tile counts across
   * all the caller's studies, while the My Studies list loads studies page by page, so its filtered
   * rows converge to the tile count once every page has loaded.
   *
   * Derived live: gathers the caller's studies, then inspects each study's recent runs (one list
   * call per study; every run item already carries its status and quarantine summary, so no per-run
   * fan-out is needed). Per-study lookups fail soft (a study whose runs can't be loaded is simply
   * not flagged) and the whole call fails soft to an empty map, so a partial ingestion outage never
   * blanks the dashboard.
   *
   * NOTE: this fans out one runs call per study (in bounded batches of
   * ATTENTION_FETCH_CONCURRENCY). That is a deliberate UI-only scope decision for this change, not
   * the end state. The proper fix is a cross-study rollup on the backend (alongside the existing
   * getStatistics aggregation) that returns the per-study attention map in one request; tracked in
   * #1004, at which point both consumers should read it instead.
   */
  async getStudiesNeedingAttention(): Promise<Map<string, StudyAttention>> {
    try {
      // Gather every study the caller can see; the runs check is per study. getDAEStudies fails soft to an
      // empty page, which ends the loop.
      const studyIds: string[] = []
      let nextToken: string | undefined
      const seenTokens = new Set<string>()
      do {
        const page = await this.getDAEStudies({ nextToken })
        for (const study of page.studies) studyIds.push(study.study_id)
        nextToken = page.nextToken
        if (nextToken && seenTokens.has(nextToken)) break
        if (nextToken) seenTokens.add(nextToken)
      } while (nextToken)

      // Fan out the per-study runs lookups in bounded batches rather than all at once, so a large
      // portfolio doesn't spike the proxy with one request per study simultaneously.
      const flagged = new Map<string, StudyAttention>()
      for (let start = 0; start < studyIds.length; start += ATTENTION_FETCH_CONCURRENCY) {
        const batch = studyIds.slice(start, start + ATTENTION_FETCH_CONCURRENCY)
        const summaries = await Promise.all(batch.map((id) => this.getStudyAttention(id)))
        batch.forEach((id, i) => {
          const summary = summaries[i]
          if (summary.failedRuns > 0 || summary.quarantinedRecords > 0) flagged.set(id, summary)
        })
      }
      return flagged
    } catch {
      return new Map<string, StudyAttention>()
    }
  }

  /**
   * Summarizes a study's ingestion runs from the last ATTENTION_WINDOW_DAYS days: how many failed /
   * partially completed and how many records were quarantined across them. A run is ignored when
   * it:
   *
   * - Is a Test Run (a validation-only run, not a Live Run data issue),
   * - Is older than the window (so a study stops being flagged once its failures age out), or
   * - Has been triaged to RESOLVED or IGNORED in its run review (the DAE has already dealt with it).
   *
   * ListRuns returns runs newest-first, so we page until the first run older than the cutoff (every
   * later run is older still) rather than capping at a fixed page size — a study with many runs
   * inside the window is never undercounted. A repeated-token guard protects against a pathological
   * non-advancing cursor. Fails soft to zeros so one study's runs outage doesn't flag it.
   */
  private async getStudyAttention(studyId: string): Promise<StudyAttention> {
    try {
      const cutoff = Date.now() - ATTENTION_WINDOW_MS
      let failedRuns = 0
      let quarantinedRecords = 0
      let nextToken: string | undefined
      let reachedWindowEnd = false
      const seenTokens = new Set<string>()
      do {
        const { runs, nextToken: token } = await ingestionApiService.listRuns(studyId, {
          nextToken,
        })
        for (const run of runs) {
          // Newest-first: the first run older than the window ends the scan for this study.
          if (run.createdAt.getTime() < cutoff) {
            reachedWindowEnd = true
            break
          }
          if (run.dryRun) continue
          if (run.reviewStatus === "RESOLVED" || run.reviewStatus === "IGNORED") continue
          if (run.status === "FAILED" || run.status === "PARTIALLY_COMPLETED") failedRuns++
          quarantinedRecords += run.quarantineSummary?.totalFailed ?? 0
        }
        nextToken = token ?? undefined
        if (nextToken && seenTokens.has(nextToken)) break
        if (nextToken) seenTokens.add(nextToken)
      } while (nextToken && !reachedWindowEnd)
      return { failedRuns, quarantinedRecords }
    } catch {
      return { failedRuns: 0, quarantinedRecords: 0 }
    }
  }

  async getDAEStudies(options?: {
    limit?: number
    nextToken?: string
  }): Promise<DaeStudiesResponse> {
    try {
      const response = await sdkCall(
        sdk.listDaeStudies({
          limit: options?.limit,
          nextToken: options?.nextToken,
        }),
      )

      const studies = (response.studies || []).map((s) => ({
        ...s,
        dta_count: s.dta_count,
        pending_dta_count: s.pending_dta_count,
      }))

      return { studies, count: response.count ?? studies.length, nextToken: response.nextToken }
    } catch {
      return { studies: [], count: 0 }
    }
  }

  /**
   * Fetches a single page of notifications with cursor-based pagination.
   */
  async getNotificationPage(options?: { limit?: number; nextToken?: string }): Promise<{
    notifications: Notification[]
    nextToken?: string
  }> {
    try {
      const response = await sdkCall(
        sdk.listDaeNotifications({ limit: options?.limit, nextToken: options?.nextToken }),
      )
      return {
        notifications: response.notifications || [],
        nextToken: response.nextToken ?? undefined,
      }
    } catch {
      return { notifications: [] }
    }
  }

  /**
   * Fetches the notification summary for the bell/page. Errors propagate so the TanStack query
   * keeps its last-known count on a transient failure instead of blinking to 0.
   *
   * ``totalCount`` is only computed server-side when ``includeTotal`` is set (a full-partition
   * count), so the 10s bell poll omits it and only the Notifications page requests it.
   */
  async getNotificationSummary(includeTotal = false): Promise<{
    unreadCount: number
    totalCount?: number
    latestId?: string
  }> {
    const response = await sdkCall(
      sdk.getNotificationSummary(includeTotal ? { includeTotal: true } : {}),
    )
    return {
      unreadCount: response.unreadCount,
      totalCount: response.totalCount ?? undefined,
      latestId: response.latestId ?? undefined,
    }
  }

  async getStudyDetails(studyId: string): Promise<Study | null> {
    try {
      const response = await sdkCall(sdk.getDaeStudyDetail({ studyId }))
      return response.study || null
    } catch {
      return null
    }
  }

  /**
   * Batch mark notifications as read. Silently skips inaccessible IDs server-side.
   *
   * The backend enforces a 100-item limit per request. If the user has more than 100 unread
   * notifications we send sequential requests to stay within that limit.
   */
  async markNotificationsAsRead(notificationIds: string[]): Promise<void> {
    const BATCH_SIZE = 100
    for (let i = 0; i < notificationIds.length; i += BATCH_SIZE) {
      const chunk = notificationIds.slice(i, i + BATCH_SIZE)
      await sdkCall(
        sdk.markNotificationsReadBatch({
          markNotificationsReadBatchRequest: { notification_ids: chunk },
        }),
      )
    }
  }
}

export const daeApiService = new DAEApiService()
